// Rein funktionaler Method-Scaffold-Generator (Plan §S5.2).
// Input: methodId, Zentrum-Koordinaten, Labels (vom Caller pre-uebersetzt), Id-Generator.
// Output: [BlockObj, ...RegionObj] in Render-Reihenfolge. KEINE Demo-Seeds.
//
// Layout-Werte sind in Worldspace-Pixeln. Sie sind bewusst grosszuegig damit
// mehrere Stickies pro Region Platz finden (Plan §S2.5: Sticky ~96px).

import type { BlockObj, CanvasObject, RegionAccepts, RegionObj } from "../types/object.js";
import type { MethodId } from "./catalog.js";

interface RegionCell {
  /** Position relativ zum Block-Ursprung. */
  x: number;
  y: number;
  width: number;
  height: number;
  accepts: RegionAccepts;
  accent?: string;
  semanticHint?: Record<string, string>;
}

interface MethodLayout {
  block: { width: number; height: number };
  /**
   * Regions in Reihenfolge — Index `i` korrespondiert zu `regionLabelKeys[i]`
   * im catalog, also zu `labels[i + 1]` im scaffold-Input (labels[0] = Block-Label).
   */
  regions: readonly RegionCell[];
}

const PADDING = 16;
const HEADER_PADDING = 28; // Platz unter Region-Label vor Sticky-Drop-Zone

/** Greifbarer Aussenrand am Block (umgibt die Regionen), damit der User den Block
 *  per Maus packen/verschieben kann ohne zwingend eine Region zu treffen. */
const BLOCK_CHROME_OUTER = 20;
/** Zusaetzlicher Innenabstand zwischen Block-Top und der ersten Region — laesst Platz
 *  fuer das Block-Label und macht die Top-Kante greifbar. */
const BLOCK_CHROME_TOP = 12;

const MOSCOW_ACCENTS = {
  must: "#c83232",
  should: "#ea5b0b",
  could: "#fdc300",
  wont: "#999999",
} as const;

const SIPOC_ACCENTS = {
  supplier: "#4a8caf",
  input: "#1f7a1f",
  process: "#ea5b0b",
  output: "#1f7a1f",
  customer: "#4a8caf",
} as const;

const REQ_CLUSTER_ACCENTS = {
  functional: "#1f7a1f",
  "non-functional": "#4a8caf",
  data: "#ea5b0b",
  "out-of-scope": "#999999",
} as const;

const PAIN_DEP_ACCENTS = {
  high: "#c83232",
  med: "#fdc300",
  low: "#999999",
} as const;

// Welle 3: lineare Prozessphasen — Links→Rechts-Farbprogression (rein visuell).
const PROCESS_PHASE_ACCENTS = ["#4a8caf", "#1f7a1f", "#fdc300", "#ea5b0b", "#c83232"] as const;
// Welle 3: Swimlane-Lanes — Rolle A / Rolle B / System.
const SWIMLANE_ACCENTS = ["#4a8caf", "#1f7a1f", "#999999"] as const;

const LAYOUTS: Record<MethodId, MethodLayout> = {
  "process-high-level": {
    block: { width: 1000, height: 200 },
    regions: [
      { x: 0, y: 0, width: 200, height: 200, accepts: "any", accent: PROCESS_PHASE_ACCENTS[0] },
      { x: 200, y: 0, width: 200, height: 200, accepts: "any", accent: PROCESS_PHASE_ACCENTS[1] },
      { x: 400, y: 0, width: 200, height: 200, accepts: "any", accent: PROCESS_PHASE_ACCENTS[2] },
      { x: 600, y: 0, width: 200, height: 200, accepts: "any", accent: PROCESS_PHASE_ACCENTS[3] },
      { x: 800, y: 0, width: 200, height: 200, accepts: "any", accent: PROCESS_PHASE_ACCENTS[4] },
    ],
  },
  "process-swimlane": {
    block: { width: 1000, height: 400 },
    regions: [
      { x: 0, y: 0, width: 1000, height: 130, accepts: "any", accent: SWIMLANE_ACCENTS[0] },
      { x: 0, y: 135, width: 1000, height: 130, accepts: "any", accent: SWIMLANE_ACCENTS[1] },
      { x: 0, y: 270, width: 1000, height: 130, accepts: "any", accent: SWIMLANE_ACCENTS[2] },
    ],
  },
  sipoc: {
    block: { width: 1000, height: 320 },
    regions: [
      {
        x: 0,
        y: 0,
        width: 200,
        height: 320,
        accepts: "any",
        accent: SIPOC_ACCENTS.supplier,
        semanticHint: { role: "supplier" },
      },
      {
        x: 200,
        y: 0,
        width: 200,
        height: 320,
        accepts: "any",
        accent: SIPOC_ACCENTS.input,
        semanticHint: { role: "input" },
      },
      {
        x: 400,
        y: 0,
        width: 200,
        height: 320,
        accepts: "any",
        accent: SIPOC_ACCENTS.process,
        semanticHint: { role: "process" },
      },
      {
        x: 600,
        y: 0,
        width: 200,
        height: 320,
        accepts: "any",
        accent: SIPOC_ACCENTS.output,
        semanticHint: { role: "output" },
      },
      {
        x: 800,
        y: 0,
        width: 200,
        height: 320,
        accepts: "any",
        accent: SIPOC_ACCENTS.customer,
        semanticHint: { role: "customer" },
      },
    ],
  },
  "pain-dependency-cluster": {
    block: { width: 700, height: 540 },
    regions: [
      // Linke Spalte: Pain — 3 Sub-Severities (oben=high, unten=low)
      {
        x: 0,
        y: 0,
        width: 350,
        height: 180,
        accepts: "pain",
        accent: PAIN_DEP_ACCENTS.high,
        semanticHint: { severity: "high" },
      },
      {
        x: 0,
        y: 180,
        width: 350,
        height: 180,
        accepts: "pain",
        accent: PAIN_DEP_ACCENTS.med,
        semanticHint: { severity: "med" },
      },
      {
        x: 0,
        y: 360,
        width: 350,
        height: 180,
        accepts: "pain",
        accent: PAIN_DEP_ACCENTS.low,
        semanticHint: { severity: "low" },
      },
      // Rechte Spalte: Dependency — 3 Sub-Severities
      {
        x: 350,
        y: 0,
        width: 350,
        height: 180,
        accepts: "dependency",
        accent: PAIN_DEP_ACCENTS.high,
        semanticHint: { severity: "high" },
      },
      {
        x: 350,
        y: 180,
        width: 350,
        height: 180,
        accepts: "dependency",
        accent: PAIN_DEP_ACCENTS.med,
        semanticHint: { severity: "med" },
      },
      {
        x: 350,
        y: 360,
        width: 350,
        height: 180,
        accepts: "dependency",
        accent: PAIN_DEP_ACCENTS.low,
        semanticHint: { severity: "low" },
      },
    ],
  },
  "requirements-cluster": {
    block: { width: 700, height: 500 },
    regions: [
      {
        x: 0,
        y: 0,
        width: 350,
        height: 250,
        accepts: "requirement",
        accent: REQ_CLUSTER_ACCENTS.functional,
        semanticHint: { category: "functional" },
      },
      {
        x: 350,
        y: 0,
        width: 350,
        height: 250,
        accepts: "requirement",
        accent: REQ_CLUSTER_ACCENTS["non-functional"],
        semanticHint: { category: "non-functional" },
      },
      {
        x: 0,
        y: 250,
        width: 350,
        height: 250,
        accepts: "requirement",
        accent: REQ_CLUSTER_ACCENTS.data,
        semanticHint: { category: "data" },
      },
      {
        x: 350,
        y: 250,
        width: 350,
        height: 250,
        accepts: "requirement",
        accent: REQ_CLUSTER_ACCENTS["out-of-scope"],
        semanticHint: { category: "out-of-scope" },
      },
    ],
  },
  moscow: {
    block: { width: 700, height: 500 },
    regions: [
      {
        x: 0,
        y: 0,
        width: 350,
        height: 250,
        accepts: "requirement",
        accent: MOSCOW_ACCENTS.must,
        semanticHint: { priority: "must" },
      },
      {
        x: 350,
        y: 0,
        width: 350,
        height: 250,
        accepts: "requirement",
        accent: MOSCOW_ACCENTS.should,
        semanticHint: { priority: "should" },
      },
      {
        x: 0,
        y: 250,
        width: 350,
        height: 250,
        accepts: "requirement",
        accent: MOSCOW_ACCENTS.could,
        semanticHint: { priority: "could" },
      },
      {
        x: 350,
        y: 250,
        width: 350,
        height: 250,
        accepts: "requirement",
        accent: MOSCOW_ACCENTS.wont,
        semanticHint: { priority: "wont" },
      },
    ],
  },
  "impact-effort": {
    block: { width: 700, height: 500 },
    regions: [
      {
        x: 0,
        y: 0,
        width: 350,
        height: 250,
        accepts: "any",
        accent: "#1f7a1f",
        semanticHint: { quadrant: "quickWins" },
      },
      {
        x: 350,
        y: 0,
        width: 350,
        height: 250,
        accepts: "any",
        accent: "#4a8caf",
        semanticHint: { quadrant: "majorProjects" },
      },
      {
        x: 0,
        y: 250,
        width: 350,
        height: 250,
        accepts: "any",
        accent: "#999999",
        semanticHint: { quadrant: "fillIns" },
      },
      {
        x: 350,
        y: 250,
        width: 350,
        height: 250,
        accepts: "any",
        accent: "#c83232",
        semanticHint: { quadrant: "thankless" },
      },
    ],
  },
  "context-target-light": {
    block: { width: 1000, height: 700 },
    regions: [
      // Zentrum-System
      { x: 300, y: 250, width: 400, height: 200, accepts: "any", accent: "#ea5b0b" },
      // Akteure oben
      { x: 100, y: 30, width: 800, height: 180, accepts: "any", accent: "#4a8caf" },
      // Systeme rechts
      { x: 720, y: 220, width: 260, height: 260, accepts: "any", accent: "#1f7a1f" },
      // Daten unten
      { x: 100, y: 480, width: 800, height: 190, accepts: "any", accent: "#fdc300" },
      // Schnittstellen links
      { x: 20, y: 220, width: 260, height: 260, accepts: "any", accent: "#999999" },
    ],
  },
};

export interface ScaffoldOptions {
  readonly methodId: MethodId;
  readonly centerX: number;
  readonly centerY: number;
  readonly createdBy: string;
  readonly createdAt: number;
  readonly newId: () => string;
  /** [block-label, region1-label, region2-label, ...]. Caller uebersetzt vorab. */
  readonly labels: readonly string[];
}

export function buildMethodScaffold(opts: ScaffoldOptions): CanvasObject[] {
  const layout = LAYOUTS[opts.methodId];
  const blockId = opts.newId();
  // Effektive Block-Geometrie inkl. greifbarem Chrome (Rand + Top-Header).
  const effectiveWidth = layout.block.width + 2 * BLOCK_CHROME_OUTER;
  const effectiveHeight = layout.block.height + 2 * BLOCK_CHROME_OUTER + BLOCK_CHROME_TOP;
  const blockX = opts.centerX - effectiveWidth / 2;
  const blockY = opts.centerY - effectiveHeight / 2;
  const innerX = blockX + BLOCK_CHROME_OUTER;
  const innerY = blockY + BLOCK_CHROME_OUTER + BLOCK_CHROME_TOP;

  const block: BlockObj = {
    id: blockId,
    type: "block",
    x: blockX,
    y: blockY,
    width: effectiveWidth,
    height: effectiveHeight,
    methodId: opts.methodId,
    label: opts.labels[0] ?? opts.methodId,
    createdBy: opts.createdBy,
    createdAt: opts.createdAt,
    parentRegionId: null,
  };

  const regions: RegionObj[] = layout.regions.map((cell, idx) => {
    const label = opts.labels[idx + 1] ?? `Region ${idx + 1}`;
    return {
      id: opts.newId(),
      type: "region",
      x: innerX + cell.x,
      y: innerY + cell.y,
      width: cell.width,
      height: cell.height,
      blockId,
      label,
      accepts: cell.accepts,
      ...(cell.accent ? { accent: cell.accent } : {}),
      ...(cell.semanticHint ? { semanticHint: cell.semanticHint } : {}),
      createdBy: opts.createdBy,
      createdAt: opts.createdAt + idx + 1,
      parentRegionId: null,
    };
  });

  return [block, ...regions];
}

/** Gibt die effektive Block-Geometrie (inkl. Chrome) zurueck — fuer Preview-Thumbnails
 *  und Drop-Zone-Suche beim Method-Insert. */
export function methodBlockSize(methodId: MethodId): { width: number; height: number } {
  const layout = LAYOUTS[methodId];
  return {
    width: layout.block.width + 2 * BLOCK_CHROME_OUTER,
    height: layout.block.height + 2 * BLOCK_CHROME_OUTER + BLOCK_CHROME_TOP,
  };
}

/** Region-Padding-Konstanten — werden im Frontend fuer Drop-Zone-Hit-Tests benoetigt. */
export const REGION_PADDING = { outer: PADDING, header: HEADER_PADDING };
